"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import GoogleButton from "../../components/GoogleButton";
import Icon, { type IconName } from "../../components/Icon";
import { ApiError, api, clearTokens, hasSession, setTokens } from "../../lib/api";
import { conNext, destinoSeguro } from "../ingresar/destino";

const ROLES = [
  {
    value: "CLIENT",
    icon: "smartphone" as IconName,
    label: "Cliente",
    desc: "Necesito reparar algo",
    resumen: "Vas a poder pedir presupuestos a los técnicos y seguir tu reparación desde el celular.",
    boton: "Crear mi cuenta de cliente",
  },
  {
    value: "TECH",
    icon: "wrench" as IconName,
    label: "Técnico",
    desc: "Reparo equipos",
    resumen: "Vas a aparecer en el buscador, recibir solicitudes y sumar cada trabajo a tu reputación.",
    boton: "Crear mi cuenta de técnico",
  },
  {
    value: "BUSINESS",
    icon: "store" as IconName,
    label: "Local",
    desc: "Tengo un negocio y derivo trabajos",
    resumen: "Vas a poder armar tu red de técnicos, derivarles reparaciones y seguirlas sin que vean a tu cliente.",
    boton: "Crear mi cuenta de local",
  },
] as const;

type RolValor = (typeof ROLES)[number]["value"];

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  roles: string[];
}

interface Me {
  id: string;
  fullName: string;
  roles: { role: string }[];
}

interface SumarRolResponse {
  ok: boolean;
  yaLoTenia: boolean;
  accessToken: string;
  refreshToken: string;
  roles: string[];
}

// Mensaje deliberadamente ambiguo: si el alta falló porque el teléfono ya existe,
// decirlo sería confirmarle a cualquiera que esa persona tiene cuenta en Busco.
const NO_PUDIMOS_INGRESAR =
  "No pudimos ingresarte. Si ya tenías cuenta con ese teléfono o email, probá recuperar tu contraseña.";

function Registro() {
  const router = useRouter();
  const params = useSearchParams();
  const next = destinoSeguro(params.get("next"), "");
  const destino = next || "/perfil";

  const [role, setRole] = useState<RolValor>("CLIENT");
  const [form, setForm] = useState({ fullName: "", phone: "", email: "", password: "", displayName: "" });
  const [error, setError] = useState("");
  const [espera, setEspera] = useState(false);
  const [busy, setBusy] = useState(false);

  // Sesión abierta: en vez de dejar que se haga una segunda cuenta (y parta su
  // reputación al medio) le ofrecemos sumar el rol a la que ya tiene.
  const [sesion, setSesion] = useState<Me | null>(null);
  const [revisandoSesion, setRevisandoSesion] = useState(true);
  const [otraCuenta, setOtraCuenta] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        if (hasSession()) {
          const yo = await api<Me>("/auth/me");
          if (vivo) setSesion(yo);
        }
      } catch (e) {
        // Token vencido o cuenta dada de baja: se limpia y se muestra el alta normal
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) clearTokens();
      }
      if (vivo) setRevisandoSesion(false);
    })();
    return () => {
      vivo = false;
    };
  }, []);

  function mostrarFalla(err: unknown, porDefecto: string) {
    setEspera(err instanceof ApiError && err.status === 429);
    setError(err instanceof Error ? err.message : porDefecto);
    setBusy(false);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setEspera(false);
    setBusy(true);
    const identifier = form.phone.trim() || form.email.trim();
    try {
      // El alta responde siempre lo mismo exista o no la cuenta (no delata teléfonos),
      // así que la sesión se consigue con un login aparte.
      await api<{ ok: boolean; mensaje: string }>("/auth/register", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          role,
          phone: form.phone || undefined,
          email: form.email || undefined,
          displayName: form.displayName || undefined,
        }),
      });
    } catch (err) {
      mostrarFalla(err, "No se pudo crear la cuenta");
      return;
    }
    try {
      const r = await api<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ identifier, password: form.password }),
      });
      setTokens(r.accessToken, r.refreshToken);
      router.replace(destino);
    } catch (err) {
      // Acá no se puede ser específico: el login pudo fallar porque la cuenta ya
      // existía con otra clave. Se mantiene todo lo escrito para que reintente.
      setEspera(err instanceof ApiError && err.status === 429);
      setError(err instanceof ApiError && err.status === 429 ? err.message : NO_PUDIMOS_INGRESAR);
      setBusy(false);
    }
  }

  async function sumarRol(rol: "TECH" | "BUSINESS") {
    setError("");
    setEspera(false);
    setBusy(true);
    try {
      const r = await api<SumarRolResponse>("/auth/sumar-rol", {
        method: "POST",
        body: JSON.stringify({ role: rol }),
      });
      // Los tokens vuelven nuevos: si no se reemplazan, el rol recién sumado no
      // vale hasta que venza el access token (una hora).
      setTokens(r.accessToken, r.refreshToken);
      router.replace(next || (rol === "TECH" ? "/tecnico" : "/local"));
    } catch (err) {
      mostrarFalla(err, "No se pudo sumar el rol");
    }
  }

  const input =
    "w-full rounded-xl bg-[#101516] border border-white/15 px-4 py-3 text-[15px] placeholder-[#808D88] focus:outline-none focus:border-[#E8A23C]";
  const label = "block text-sm text-[#B5C0BB] mb-1.5";
  const elegido = ROLES.find((r) => r.value === role) ?? ROLES[0];

  const aviso = error && (
    <p
      role="alert"
      className={`text-sm rounded-lg px-4 py-2.5 flex items-start gap-2 ${
        espera ? "text-[#E8A23C] bg-[#E8A23C]/10" : "text-[#DE705C] bg-[#DE705C]/10"
      }`}
    >
      <Icon name={espera ? "clock" : "alert"} size={16} className="mt-0.5 shrink-0" />
      <span>
        {error}
        {error === NO_PUDIMOS_INGRESAR && (
          <>
            {" "}
            <Link href={conNext("/recuperar", next)} className="underline font-semibold">
              Recuperar contraseña
            </Link>
          </>
        )}
      </span>
    </p>
  );

  if (revisandoSesion) {
    return (
      <main className="min-h-screen bg-[#14191B] grid place-items-center p-6">
        <div className="w-10 h-10 rounded-full border-2 border-white/15 border-t-[#E8A23C] animate-spin" />
      </main>
    );
  }

  if (sesion && !otraCuenta) {
    const tiene = (r: string) => sesion.roles.some((x) => x.role === r);
    const faltan = (["TECH", "BUSINESS"] as const).filter((r) => !tiene(r));
    return (
      <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] flex items-center justify-center p-6">
        <div className="w-full max-w-md flex flex-col gap-5">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] text-[#E8A23C] uppercase mb-2">Busco · tu cuenta</p>
            <h1 className="text-2xl font-bold tracking-tight">Ya tenés una cuenta abierta</h1>
            <p className="mt-2 text-[15px] text-[#B5C0BB] leading-relaxed">
              Estás dentro como <span className="text-[#EDF1EF] font-semibold">{sesion.fullName}</span>. No hace
              falta que crees otra: sumale el rol que necesitás y todo queda en el mismo perfil. Con dos cuentas
              distintas, tus calificaciones y tu historial quedan partidos al medio.
            </p>
          </div>

          {aviso}

          {faltan.length === 0 ? (
            <p className="text-sm text-[#B5C0BB] bg-white/5 rounded-xl px-4 py-3">
              Tu cuenta ya trabaja como técnico y como local: no hay nada más que sumar.
            </p>
          ) : (
            faltan.map((r) => {
              const info = ROLES.find((x) => x.value === r);
              if (!info) return null;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => sumarRol(r)}
                  disabled={busy}
                  className="text-left rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 transition p-4 flex items-start gap-3 disabled:opacity-50"
                >
                  <Icon name={info.icon} size={22} className="text-[#E8A23C] mt-0.5 shrink-0" />
                  <span>
                    <span className="block font-semibold">Sumar el rol de {info.label.toLowerCase()}</span>
                    <span className="block text-sm text-[#808D88] mt-0.5 leading-snug">{info.resumen}</span>
                  </span>
                </button>
              );
            })
          )}

          <Link href={destino} className="rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3.5 text-center hover:brightness-105 transition">
            Seguir con mi cuenta
          </Link>
          <button
            type="button"
            onClick={() => {
              clearTokens();
              setSesion(null);
              setOtraCuenta(true);
              setError("");
            }}
            className="text-sm text-center text-[#808D88] hover:text-[#E8A23C]"
          >
            No soy yo: cerrar sesión y crear otra cuenta
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-md flex flex-col gap-5">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] text-[#E8A23C] uppercase mb-2">Busco · crear cuenta</p>
          <h1 className="text-3xl font-bold tracking-tight">Sumate a la red</h1>
        </div>

        <div>
          <p className={label} id="tipo-de-cuenta">¿Qué tipo de cuenta querés?</p>
          <div className="grid grid-cols-3 gap-2" role="group" aria-labelledby="tipo-de-cuenta">
            {ROLES.map((r) => (
              <button
                key={r.value}
                type="button"
                aria-pressed={role === r.value}
                onClick={() => setRole(r.value)}
                className={`rounded-xl border p-3 text-center transition ${
                  role === r.value
                    ? "border-[#E8A23C] bg-[#E8A23C]/10"
                    : "border-white/15 bg-white/5 hover:bg-white/10"
                }`}
              >
                <Icon name={r.icon} size={22} className={`mx-auto ${role === r.value ? "text-[#E8A23C]" : "text-[#B5C0BB]"}`} />
                <div className="text-sm font-semibold mt-1.5">{r.label}</div>
                <div className="text-[11px] text-[#808D88] mt-0.5 leading-tight">{r.desc}</div>
              </button>
            ))}
          </div>
          {/* Que quede claro qué se está creando: el rol define media app */}
          <p className="mt-2 text-sm text-[#B5C0BB] leading-snug">
            Estás creando una cuenta de <span className="text-[#EDF1EF] font-semibold">{elegido.label}</span>.{" "}
            {elegido.resumen}
          </p>
        </div>

        <div>
          <label htmlFor="nombre" className={label}>Tu nombre completo</label>
          <input id="nombre" name="nombre" className={input} placeholder="Ej.: María González" value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            autoComplete="name" autoCapitalize="words" enterKeyHint="next" required />
        </div>

        <div>
          <label htmlFor="telefono" className={label}>Teléfono</label>
          <input id="telefono" name="telefono" className={input} placeholder="09xxxxxxxx" type="tel"
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            autoComplete="tel" inputMode="tel" enterKeyHint="next" required={!form.email} />
        </div>

        <div>
          <label htmlFor="email" className={label}>Email <span className="text-[#808D88]">(opcional si pusiste teléfono)</span></label>
          <input id="email" name="email" className={input} placeholder="tu@email.com" type="email"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
            enterKeyHint="next" required={!form.phone} />
        </div>

        {role !== "CLIENT" && (
          <div>
            <label htmlFor="nombre-comercial" className={label}>
              {role === "TECH" ? "Nombre de tu taller" : "Nombre de tu local"}
            </label>
            <input id="nombre-comercial" name="nombre-comercial" className={input}
              placeholder={role === "TECH" ? "Ej.: TecnoFix" : "Ej.: Celulares del Centro"}
              value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              autoComplete="organization" enterKeyHint="next" />
          </div>
        )}

        <div>
          <label htmlFor="clave-nueva" className={label}>Contraseña <span className="text-[#808D88]">(mínimo 8 caracteres)</span></label>
          <input id="clave-nueva" name="clave-nueva" className={input} type="password" placeholder="Elegí una contraseña"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="new-password" enterKeyHint="go" required minLength={8} />
        </div>

        {aviso}

        <button disabled={busy}
          className="rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3.5 hover:brightness-105 transition disabled:opacity-50">
          {busy ? "Creando cuenta…" : elegido.boton}
        </button>
        <GoogleButton next={next} />
        <p className="text-sm text-center text-[#808D88]">
          ¿Ya tenés cuenta?{" "}
          <Link href={conNext("/ingresar", next)} className="text-[#E8A23C] font-semibold">Ingresá</Link>
        </p>
      </form>
    </main>
  );
}

// useSearchParams necesita un límite de Suspense para que la página siga siendo
// prerenderizable; el fallback es el fondo pelado para que no parpadee en blanco.
export default function RegistroPage() {
  return (
    <Suspense fallback={<main className="min-h-screen bg-[#14191B]" />}>
      <Registro />
    </Suspense>
  );
}
